import {
  findProductPage,
  PRODUCT_CATALOG_PATH,
  PRODUCT_PAGE_PREFIX,
} from "./product-pages/product-pages-content";
import { WORK_GALLERY_PATH } from "./work-gallery/work-gallery-api";
import { CONTACT_PATH } from "./public-routes";
import { PRODUCT_DESIGNER_PREVIEW_PATH } from "./storefront-routes";

export const SITE_ORIGIN = "https://happyprint.co.il";
export const SITE_NAME = "בית דפוס רק בשמחות";

const DEFAULT_DESCRIPTION =
  "בית דפוס ושילוט בקריית ביאליק להזמנת שלטים, מדבקות, רולאפים, מוצרי דפוס ומיתוג לעסקים, מוסדות ואירועים.";

export type RouteMetadata = {
  title: string;
  description: string;
  canonicalUrl: string;
  robots: "index, follow" | "noindex, nofollow";
  type: "website" | "product";
};

function normalizeRoutePath(pathname: string): string {
  const pathOnly = pathname.split(/[?#]/, 1)[0] || "/";
  const withLeadingSlash = pathOnly.startsWith("/") ? pathOnly : `/${pathOnly}`;
  const normalized = withLeadingSlash.replace(/\/+$/, "");
  return normalized || "/";
}

function canonicalUrl(path: string): string {
  return new URL(path, `${SITE_ORIGIN}/`).toString();
}

function publicPage(
  path: string,
  title: string,
  description: string,
  type: RouteMetadata["type"] = "website",
): RouteMetadata {
  return {
    title,
    description,
    canonicalUrl: canonicalUrl(path),
    robots: "index, follow",
    type,
  };
}

function privatePage(title: string): RouteMetadata {
  return {
    title,
    description: "עמוד מאובטח ללקוחות בית דפוס רק בשמחות.",
    // Never expose order codes, proof tokens, or other transactional paths in
    // canonical metadata even if a crawler or browser extension reads the DOM.
    canonicalUrl: canonicalUrl("/"),
    robots: "noindex, nofollow",
    type: "website",
  };
}

export function resolveRouteMetadata(pathname: string): RouteMetadata {
  const path = normalizeRoutePath(pathname);

  if (path === "/") {
    return publicPage(
      "/",
      `${SITE_NAME} | דפוס ושילוט בקריות`,
      DEFAULT_DESCRIPTION,
    );
  }

  if (path === "/privacy") {
    return publicPage(
      path,
      `מדיניות פרטיות | ${SITE_NAME}`,
      "מידע על סוגי הנתונים שהאתר מעבד לצורך חשבון לקוח, הזמנות, קבצים, הצעות מחיר ושירות.",
    );
  }

  if (path === "/accessibility") {
    return publicPage(
      path,
      `הצהרת נגישות | ${SITE_NAME}`,
      "מידע על התאמות הנגישות הקיימות באתר, סטטוס הבדיקות ודרכי פנייה במקרה של קושי.",
    );
  }

  if (path === PRODUCT_CATALOG_PATH) {
    return publicPage(
      path,
      `מוצרי דפוס ושילוט | ${SITE_NAME}`,
      "מוצרי דפוס, שילוט ומיתוג לעסקים ולאירועים: שלטים לפי מידה, רולאפים, מדבקות, קאפה, שמשוניות, הזמנות וחולצות מודפסות.",
    );
  }

  if (path === WORK_GALLERY_PATH) {
    return publicPage(
      path,
      `העבודות שלנו | ${SITE_NAME}`,
      "גלריית עבודות של בית דפוס רק בשמחות: שילוט לעסקים, רולאפים, קאפות, שלטי PVC, שמשוניות, מדבקות, מתקני תצוגה וקירות צילום לאירועים.",
    );
  }

  if (path === CONTACT_PATH) {
    return publicPage(
      path,
      `צור קשר | ${SITE_NAME}`,
      "צרו קשר עם בית דפוס רק בשמחות בקריית ביאליק לקבלת עזרה, הצעת מחיר, מידע על איסוף, שעות פעילות והכוונה להזמנה.",
    );
  }

  if (path === "/pvc-sign") {
    return publicPage(
      path,
      `שלט PVC לפי מידה ומחיר | ${SITE_NAME}`,
      "מזמינים שלט PVC לפי מידה, עובי, צורה וגימור ומקבלים מחיר מיידי לפני ההוספה לסל.",
      "product",
    );
  }

  if (path.startsWith(PRODUCT_PAGE_PREFIX)) {
    const slug = path.slice(PRODUCT_PAGE_PREFIX.length);
    const product = findProductPage(slug);
    if (product) {
      return publicPage(
        path,
        `${product.title} | ${SITE_NAME}`,
        product.summary,
        "product",
      );
    }
    // A server-discovered product may legitimately exist at this path, but we
    // must not index arbitrary /products/* URLs before the Shop API confirms
    // that the product + public HappyPrint config exist.
    return {
      title: `מוצר | ${SITE_NAME}`,
      description: DEFAULT_DESCRIPTION,
      canonicalUrl: canonicalUrl(path),
      robots: "noindex, nofollow",
      type: "product",
    };
  }

  if (path === "/cart") return privatePage(`סל הקניות | ${SITE_NAME}`);
  if (path === "/checkout") return privatePage(`השלמת הזמנה | ${SITE_NAME}`);
  if (path === "/login") return privatePage(`כניסה לחשבון | ${SITE_NAME}`);
  if (path === "/register") return privatePage(`פתיחת חשבון | ${SITE_NAME}`);
  if (path === PRODUCT_DESIGNER_PREVIEW_PATH) {
    return {
      title: `תצוגת עורך העיצובים | ${SITE_NAME}`,
      description: "תצוגה מקדימה של עורך העיצובים למוצרי דפוס.",
      canonicalUrl: canonicalUrl("/"),
      robots: "noindex, nofollow",
      type: "website",
    };
  }
  if (path === "/account" || path.startsWith("/account/")) {
    return privatePage(`האזור האישי | ${SITE_NAME}`);
  }
  if (
    path === "/order-confirmation" ||
    path.startsWith("/artwork-upload/") ||
    path.startsWith("/proof/") ||
    path.startsWith("/product-designer/")
  ) {
    return privatePage(`פרטי הזמנה | ${SITE_NAME}`);
  }

  return {
    title: `העמוד לא נמצא | ${SITE_NAME}`,
    description: DEFAULT_DESCRIPTION,
    canonicalUrl: canonicalUrl("/"),
    robots: "noindex, nofollow",
    type: "website",
  };
}

function setMeta(
  documentRef: Document,
  attribute: "name" | "property",
  key: string,
  value: string,
): void {
  let element = documentRef.head.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${key}"]`,
  );
  if (!element) {
    element = documentRef.createElement("meta");
    element.setAttribute(attribute, key);
    documentRef.head.append(element);
  }
  element.content = value;
}

function writeRouteMetadata(
  metadata: RouteMetadata,
  documentRef: Document,
): RouteMetadata {
  documentRef.title = metadata.title;

  setMeta(documentRef, "name", "description", metadata.description);
  setMeta(documentRef, "name", "robots", metadata.robots);
  setMeta(documentRef, "property", "og:locale", "he_IL");
  setMeta(documentRef, "property", "og:site_name", SITE_NAME);
  setMeta(documentRef, "property", "og:type", metadata.type);
  setMeta(documentRef, "property", "og:title", metadata.title);
  setMeta(documentRef, "property", "og:description", metadata.description);
  setMeta(documentRef, "property", "og:url", metadata.canonicalUrl);
  setMeta(documentRef, "name", "twitter:card", "summary");
  setMeta(documentRef, "name", "twitter:title", metadata.title);
  setMeta(documentRef, "name", "twitter:description", metadata.description);

  let canonical = documentRef.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!canonical) {
    canonical = documentRef.createElement("link");
    canonical.rel = "canonical";
    documentRef.head.append(canonical);
  }
  canonical.href = metadata.canonicalUrl;

  return metadata;
}

export function applyRouteMetadata(
  pathname: string,
  documentRef: Document = document,
): RouteMetadata {
  return writeRouteMetadata(resolveRouteMetadata(pathname), documentRef);
}

export function applyDynamicProductMetadata(
  slug: string,
  title: string,
  description: string,
  documentRef: Document = document,
): RouteMetadata {
  const safePath = `${PRODUCT_PAGE_PREFIX}${encodeURIComponent(slug)}`;
  return writeRouteMetadata(
    publicPage(
      safePath,
      title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`,
      description,
      "product",
    ),
    documentRef,
  );
}
